/**
 * Password Validation Utility for AVA Web App
 * Enforces strong password requirements and provides strength analysis
 */

// Common weak passwords to block
const COMMON_WEAK_PASSWORDS = [
  'password', 'password1', 'password12', 'password123', 'password1234',
  'qwerty', 'qwerty123', 'qwertyuiop',
  '12345678', '123456789', '1234567890',
  'abc123', 'abcd1234', 'admin', 'admin123',
  'letmein', 'welcome', 'welcome123',
  'monkey', 'dragon', 'master',
  'sunshine', 'princess', 'football',
  'iloveyou', 'trustno1', '1234qwer',
  'qwer1234', 'asdf1234', 'zxcv1234',
  'pass1234', 'test1234', 'hello123'
];

// Password validation criteria
const PASSWORD_CRITERIA = {
  minLength: {
    test: (password) => password.length >= 8,
    message: 'At least 8 characters',
    icon: '📏'
  },
  maxLength: {
    test: (password) => password.length <= 128,
    message: 'Maximum 128 characters',
    icon: '📐'
  },
  uppercase: {
    test: (password) => /[A-Z]/.test(password),
    message: 'One uppercase letter (A-Z)',
    icon: '🔠'
  },
  lowercase: {
    test: (password) => /[a-z]/.test(password),
    message: 'One lowercase letter (a-z)',
    icon: '🔡'
  },
  number: {
    test: (password) => /[0-9]/.test(password),
    message: 'One number (0-9)',
    icon: '🔢'
  },
  specialChar: {
    test: (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    message: 'One special character (!@#$...)',
    icon: '🔣'
  },
  noRepeating: {
    test: (password) => !/(.)\1{2,}/.test(password),
    message: 'No repeating characters (aaa, 111)',
    icon: '🚫'
  },
  notCommon: {
    test: (password) => !COMMON_WEAK_PASSWORDS.includes(password.toLowerCase()),
    message: 'Not a common password',
    icon: '🔒'
  }
};

/**
 * Validate password against all criteria
 * @param {string} password - The password to validate
 * @returns {object} Validation result with details
 */
export function validatePassword(password) {
  if (!password) {
    return {
      isValid: false,
      strength: 0,
      strengthLevel: 'none',
      criteria: {},
      message: 'Password is required'
    };
  }

  const results = {};
  let passedCriteria = 0;
  let failedMessages = [];

  // Check each criterion
  for (const [key, criterion] of Object.entries(PASSWORD_CRITERIA)) {
    const passed = criterion.test(password);
    results[key] = {
      passed,
      message: criterion.message,
      icon: criterion.icon
    };
    
    if (passed) {
      passedCriteria++;
    } else {
      // Only add to failed messages if it's not noRepeating or notCommon (for user display)
      if (key !== 'noRepeating' && key !== 'notCommon') {
        failedMessages.push(criterion.message);
      }
    }
  }

  // Calculate strength level
  let strengthLevel = 'weak';
  let strengthColor = '#ef4444'; // red
  
  if (passedCriteria >= 7) {
    strengthLevel = 'strong';
    strengthColor = '#22c55e'; // green
  } else if (passedCriteria >= 5) {
    strengthLevel = 'medium';
    strengthColor = '#eab308'; // yellow
  } else if (passedCriteria >= 3) {
    strengthLevel = 'fair';
    strengthColor = '#f97316'; // orange
  }

  // Check if all required criteria are met (excluding maxLength and noRepeating for basic validation)
  const requiredCriteria = ['minLength', 'uppercase', 'lowercase', 'number', 'specialChar', 'notCommon'];
  const isValid = requiredCriteria.every(key => results[key].passed) && results.maxLength.passed;

  return {
    isValid,
    strength: (passedCriteria / Object.keys(PASSWORD_CRITERIA).length) * 100,
    strengthLevel,
    strengthColor,
    criteria: results,
    passedCount: passedCriteria,
    totalCount: Object.keys(PASSWORD_CRITERIA).length,
    message: isValid ? 'Password meets all requirements' : `Missing: ${failedMessages.join(', ')}`
  };
}

/**
 * Check if passwords match
 * @param {string} password - The original password
 * @param {string} confirmPassword - The confirmation password
 * @returns {object} Match result
 */
export function checkPasswordMatch(password, confirmPassword) {
  if (!confirmPassword) {
    return {
      matches: false,
      message: 'Please confirm your password'
    };
  }

  const matches = password === confirmPassword;
  return {
    matches,
    message: matches ? 'Passwords match' : "Passwords don't match"
  };
}

/**
 * Get password strength percentage and label
 * @param {string} password - The password to analyze
 * @returns {object} Strength analysis
 */
export function getPasswordStrength(password) {
  const validation = validatePassword(password);
  
  return {
    percentage: validation.strength,
    level: validation.strengthLevel,
    color: validation.strengthColor,
    label: getStrengthLabel(validation.strengthLevel)
  };
}

/**
 * Get human-readable strength label
 * @param {string} level - The strength level
 * @returns {string} Human-readable label
 */
function getStrengthLabel(level) {
  const labels = {
    none: 'Enter a password',
    weak: 'Weak password',
    fair: 'Fair password',
    medium: 'Good password',
    strong: 'Strong password'
  };
  
  return labels[level] || 'Enter a password';
}

/**
 * Generate password hint text
 * @returns {string} Password requirement hint
 */
export function getPasswordHint() {
  return 'Min 8 chars, uppercase, lowercase, number, special char (!@#$...)';
}

/**
 * Format Firebase error messages
 * @param {string} errorCode - Firebase error code
 * @returns {string} User-friendly error message
 */
export function formatAuthError(errorCode) {
  const errorMessages = {
    'auth/weak-password': "Password doesn't meet security requirements",
    'auth/email-already-in-use': 'Account already exists with this email',
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled',
    'auth/network-request-failed': 'Network error. Please check your connection',
    'auth/too-many-requests': 'Too many attempts. Please try again later',
    'auth/user-disabled': 'This account has been disabled',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/invalid-credential': 'Invalid login credentials',
    'auth/missing-password': 'Password is required',
    'auth/missing-email': 'Email is required'
  };
  
  return errorMessages[errorCode] || 'An error occurred. Please try again.';
}

/**
 * Sanitize email for validation
 * @param {string} email - The email to sanitize
 * @returns {string} Sanitized email
 */
export function sanitizeEmail(email) {
  return email ? email.trim().toLowerCase() : '';
}

/**
 * Validate email format
 * @param {string} email - The email to validate
 * @returns {boolean} Is valid email
 */
export function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Get validation criteria for display (excludes backend-only checks)
 * @returns {array} Array of criteria objects
 */
export function getAllCriteria() {
  return Object.entries(PASSWORD_CRITERIA)
    .filter(([key]) => key !== 'noRepeating' && key !== 'notCommon')
    .map(([key, criterion]) => ({
      key,
      message: criterion.message,
      icon: criterion.icon
    }));
}

const passwordValidation = {
  validatePassword,
  checkPasswordMatch,
  getPasswordStrength,
  getPasswordHint,
  formatAuthError,
  sanitizeEmail,
  validateEmail,
  getAllCriteria
};
export default passwordValidation;